import { useState } from 'react';
import {
  Button,
  Card,
  Tag,
  Badge,
  Avatar,
  AvatarGroup,
  Modal,
  Drawer,
  Tabs,
  Tooltip,
  Skeleton,
  Pagination,
  DatePicker,
  StatCard,
  Empty,
  Breadcrumbs,
  toast,
} from '../../components/ds';

const Section: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <section className="border-t border-line pt-8">
    <div className="mb-4">
      <h2 className="text-base font-bold uppercase tracking-label text-fg-muted">{title}</h2>
      {description && <p className="mt-1 text-xs text-fg-secondary">{description}</p>}
    </div>
    <div>{children}</div>
  </section>
);

export default function ComponentsGallery() {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState('a');
  const [page, setPage] = useState(2);
  const [date, setDate] = useState<Date | undefined>(new Date());

  return (
    <div className="min-h-screen bg-bg-deeper p-6 md:p-10 text-fg-primary">
      <header className="max-w-6xl mx-auto mb-10">
        <Breadcrumbs items={[{ label: 'Dev' }, { label: 'Components' }]} />
        <h1 className="mt-2 text-3xl font-extrabold tracking-heading">Components Gallery</h1>
        <p className="mt-2 text-fg-secondary text-sm">
          support-crm design system · все варианты и состояния. Этот маршрут видим только в DEV или
          с <code>?dev=1</code>.
        </p>
      </header>

      <main className="max-w-6xl mx-auto space-y-10">

        <Section title="01 · Button">
          <div className="flex flex-wrap gap-3 items-center">
            <Button variant="primary">+ Новый</Button>
            <Button variant="secondary">Фильтр</Button>
            <Button variant="danger">Удалить</Button>
            <Button variant="text">Отмена</Button>
            <Button size="sm">SM</Button>
            <Button size="md">MD</Button>
            <Button size="lg">LG</Button>
            <Button loading>Загрузка…</Button>
            <Button disabled>Disabled</Button>
          </div>
        </Section>

        <Section title="02 · Card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><h4 className="font-bold mb-1">Default</h4><p className="text-sm text-fg-secondary">Базовый вариант карточки.</p></Card>
            <Card variant="elevated"><h4 className="font-bold mb-1">Elevated</h4><p className="text-sm text-fg-secondary">С тенью.</p></Card>
            <Card variant="ghost" hoverable><h4 className="font-bold mb-1">Ghost (hoverable)</h4><p className="text-sm text-fg-secondary">Прозрачный фон.</p></Card>
          </div>
        </Section>

        <Section title="03 · Input / Textarea (via .input-field class)">
          <div className="flex flex-col gap-2 max-w-md">
            <input className="input-field" placeholder="Обычное поле" />
            <input className="input-field" defaultValue="С фокусом — наведи Tab" />
            <input className="input-field input-error" defaultValue="С ошибкой" />
            <textarea className="input-field" rows={3} placeholder="Многострочное поле" />
          </div>
        </Section>

        <Section title="04 · Tag">
          <div className="flex flex-wrap gap-2 items-center">
            <Tag>Telegram</Tag>
            <Tag dot="pulse">Live</Tag>
            <Tag variant="accent">В работе</Tag>
            <Tag variant="danger">Просрочен</Tag>
            <Tag variant="success">Решён</Tag>
            <Tag closable onClose={() => toast('closed')}>Closable</Tag>
          </div>
        </Section>

        <Section title="05 · Table (page-level pattern)">
          <div className="rounded-xl border border-line bg-bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-bg-elevated">
                <tr>
                  <th className="text-left p-3 text-[10px] uppercase tracking-label text-fg-muted">#</th>
                  <th className="text-left p-3 text-[10px] uppercase tracking-label text-fg-muted">Тема</th>
                  <th className="text-left p-3 text-[10px] uppercase tracking-label text-fg-muted">Статус</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-line bg-fill-2 [box-shadow:inset_3px_0_0_theme(colors.accent.DEFAULT)]">
                  <td className="p-3">#4218</td><td className="p-3">Не запускается клиент</td>
                  <td className="p-3"><Tag variant="accent">В работе</Tag></td>
                </tr>
                <tr className="border-t border-line">
                  <td className="p-3">#4217</td><td className="p-3">Списало с депозита</td>
                  <td className="p-3"><Tag variant="danger">Просрочен</Tag></td>
                </tr>
                <tr className="border-t border-line">
                  <td className="p-3">#4216</td><td className="p-3">Вопрос про абонемент</td>
                  <td className="p-3"><Tag variant="success">Решён</Tag></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="06 · Sidebar + TopBar (in-page rehearsal)" description="Полноценный shell живёт в ClientLayout — здесь миниатюра">
          <div className="flex gap-3">
            <div className="w-44 rounded-xl border border-line bg-bg-card p-2 flex flex-col gap-1">
              {['Тикеты', 'Клиенты', 'База знаний'].map((it, i) => (
                <div
                  key={it}
                  className={i === 0
                    ? 'px-3 py-2 rounded-lg bg-accent-bg text-accent text-sm font-semibold'
                    : 'px-3 py-2 rounded-lg text-fg-secondary text-sm font-medium'}
                >
                  {it}
                </div>
              ))}
            </div>
            <div className="flex-1 rounded-xl border border-line bg-bg-card p-3 flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-accent text-accent-text inline-flex items-center justify-center font-extrabold text-sm">R</div>
              <div className="flex-1 rounded-lg bg-fill-2 border border-line px-3 py-1.5 text-xs text-fg-muted">Поиск…</div>
              <Avatar size="sm" name="Никита Петров" />
            </div>
          </div>
        </Section>

        <Section title="07 · Modal + Drawer">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
            <Button variant="secondary" onClick={() => setDrawerOpen(true)}>Open Drawer</Button>
          </div>
          <Modal
            open={modalOpen}
            onOpenChange={setModalOpen}
            title="Закрыть тикет"
            footer={
              <>
                <Button variant="text" onClick={() => setModalOpen(false)}>Отмена</Button>
                <Button onClick={() => { toast.success('Закрыт'); setModalOpen(false); }}>Закрыть</Button>
              </>
            }
          >
            <p className="text-sm text-fg-secondary">
              Тикет будет помечен решённым. Клиент получит уведомление с просьбой оценить.
            </p>
          </Modal>
          <Drawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            title="Детали тикета"
          >
            <p className="text-sm text-fg-secondary">Drawer body content — справа сбоку.</p>
          </Drawer>
        </Section>

        <Section title="08 · Tabs">
          <Tabs
            value={tab}
            onValueChange={setTab}
            items={[
              { value: 'a', label: 'Все', content: <p className="text-sm text-fg-secondary">Tab A content</p> },
              { value: 'b', label: 'Открытые', badge: <Badge>12</Badge>, content: <p className="text-sm text-fg-secondary">Tab B content</p> },
              { value: 'c', label: 'Просроченные', badge: <Badge variant="gray">3</Badge>, content: <p className="text-sm text-fg-secondary">Tab C content</p> },
              { value: 'd', label: 'Решённые', content: <p className="text-sm text-fg-secondary">Tab D content</p> },
            ]}
          />
        </Section>

        <Section title="09 · Badge">
          <div className="flex items-center gap-4">
            <span className="text-sm">Уведомления <Badge>8</Badge></span>
            <span className="text-sm">Inbox <Badge variant="gray">142</Badge></span>
          </div>
        </Section>

        <Section title="10 · Avatar + Group">
          <div className="flex items-center gap-6">
            <Avatar size="xs" name="НП" />
            <Avatar size="sm" name="АК" />
            <Avatar size="md" name="ВТ" />
            <Avatar size="lg" name="ДГ" />
            <Avatar size="xl" name="РБ" />
            <AvatarGroup>
              <Avatar size="md" name="НП" />
              <Avatar size="md" name="АК" />
              <Avatar size="md" name="ВТ" />
              <Avatar size="md" name="ДГ" />
              <Avatar size="md" name="РБ" />
              <Avatar size="md" name="ИИ" />
            </AvatarGroup>
          </div>
        </Section>

        <Section title="11 · Tooltip">
          <div className="flex items-center gap-3 py-4">
            <Tooltip content="SLA: 2ч 13м">
              <div className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-line bg-bg-elevated text-fg-secondary text-base">⏱</div>
            </Tooltip>
            <Tooltip content="Обновить" placement="bottom">
              <div className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-line bg-bg-elevated text-fg-secondary">↻</div>
            </Tooltip>
            <Tooltip content="Меню" placement="right">
              <div className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-line bg-bg-elevated text-fg-secondary">⋯</div>
            </Tooltip>
          </div>
        </Section>

        <Section title="12 · Skeleton">
          <div className="flex flex-col gap-2 max-w-md">
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="90%" height={10} />
            <Skeleton variant="text" width="75%" height={10} />
            <div className="flex items-center gap-3 mt-2">
              <Skeleton variant="circle" width={36} />
              <div className="flex-1 flex flex-col gap-2">
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="80%" height={10} />
              </div>
            </div>
          </div>
        </Section>

        <Section title="13 · Toast">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => toast.success('Тикет #4218 закрыт', { description: 'SLA соблюдён · 1ч 47м' })}>Success toast</Button>
            <Button variant="danger" onClick={() => toast.error('Telegram canal disconnected', { description: 'Перепривяжите бота' })}>Error toast</Button>
            <Button variant="secondary" onClick={() => toast('Inline notice')}>Info toast</Button>
          </div>
        </Section>

        <Section title="14 · Pagination">
          <Pagination page={page} total={14} onChange={setPage} />
        </Section>

        <Section title="15 · DatePicker">
          <DatePicker mode="single" selected={date} onSelect={setDate} />
        </Section>

        <Section title="16 · StatCard">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard label="Открыто" value="142" delta={{ value: '8 за сутки', up: true }} sparkline={[3, 5, 4, 7, 6, 9, 8, 12]} />
            <StatCard label="SLA OK" value="96%" delta={{ value: '1.2%', up: true }} />
            <StatCard label="Среднее" value="23м" delta={{ value: '3м', up: false }} />
          </div>
        </Section>

        <Section title="17 · Empty">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Empty title="Нет открытых тикетов" description="Все обращения обработаны. Передохните пару минут." />
            <Empty
              border="solid"
              icon="!"
              title="Ошибка загрузки"
              description="Не удалось получить данные. Попробуйте обновить страницу."
              cta={<Button size="sm">Повторить</Button>}
            />
          </div>
        </Section>

        <Section title="18 · Breadcrumbs">
          <Breadcrumbs items={[
            { label: 'Поддержка', href: '#' },
            { label: 'Тикеты', href: '#' },
            { label: '#4218 — Не запускается клиент' },
          ]} />
        </Section>

      </main>
    </div>
  );
}
